
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Trip } from '@/types/itinerary';
import { toast } from 'sonner';
import { User } from '@supabase/supabase-js';

interface UseTripDetailsResult {
  trip: Trip | null;
  isLoading: boolean;
  isAuthorized: boolean;
}

export const useTripDetails = (tripId: string | undefined, user: User | null): UseTripDetailsResult => {
  const [trip, setTrip] = useState<Trip | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!tripId) {
      toast.error("No trip selected");
      navigate('/trips');
      return;
    }
    
    const fetchTripDetails = async () => {
      setIsLoading(true);
      try {
        const { data: tripData, error: tripError } = await supabase
          .from('trips')
          .select('*')
          .eq('id', tripId)
          .single();
        
        if (tripError) throw tripError;
        
        if (!tripData) {
          toast.error("Trip not found");
          navigate('/trips');
          return;
        }
        
        setTrip(tripData);
        
        if (user) {
          if (tripData.owner_id === user.id) {
            setIsAuthorized(true);
          } else {
            const { data: memberData, error: memberError } = await supabase
              .from('trip_members')
              .select('*')
              .eq('trip_id', tripId)
              .eq('user_id', user.id)
              .single();
            
            setIsAuthorized(!!memberData);
            
            if (memberError && memberError.code !== 'PGRST116') {
              console.error("Error checking membership:", memberError);
            }
          }
        } else {
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error("Error fetching trip:", error);
        toast.error("Could not load trip details");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTripDetails();
  }, [tripId, user, navigate]);
  
  return { trip, isLoading, isAuthorized };
};
